import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { T } from '../../../../Translations';
import Modal from '../../../Modal';
import BigCheckmarkCircleIcon from '../../../Cutesies/BigCheckmarkCircleIcon';
import { participantStore } from './Stores/ParticipantStore';

function ParticipantAdded() {
  const navigate = useNavigate();

  const close = () => {
    // Reset only participant identity. Group and license selections in formStore
    // persist intentionally so admins can add several people to the same set in
    // one session without re-selecting each time.
    participantStore.reset();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const returnToList = () => {
    participantStore.reset();
    navigate('/people/participants');
  };

  switch (participantStore.state.kind) {
    case 'ready':
    case 'looking-for-participant':
    case 'creating':
    case 'directory-lookup-error':
    case 'error':
      return <></>;
    case 'confirming':
      const isExisting = participantStore.state.isExistingParticipant.getOrElse(() => false);
      const groupCount = participantStore.state.participant.userGroups.filter(
        (g) => g.kind === 'selected',
      ).length;
      const licenseCount = participantStore.state.participant.licenses.filter(
        (l) => l.kind === 'selected',
      ).length;
      const initialGroupCount = participantStore.state.initialGroupCount.getOrElse(() => 0);
      const initialLicenseCount = participantStore.state.initialLicenseCount.getOrElse(() => 0);

      return (
        <Modal close={close} title="">
          <div className="text-center">
            {/* Success Icon */}
            <div className="flex justify-center mb-4">
              <BigCheckmarkCircleIcon width="80" height="80" />
            </div>

            {/* Title */}
            <h2 className="text-xl mb-0.5" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
              {isExisting ? 'Participant Updated' : 'Participant Added'}
            </h2>

            {/* Name & Email */}
            <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
              {participantStore.state.participant.firstName}{' '}
              {participantStore.state.participant.lastName} ·{' '}
              {participantStore.state.participant.email}
            </p>

            {/* Details Box */}
            <div className="bg-muted rounded-lg px-3.5 py-2.5 mb-4">
              {!isExisting ? (
                // NEW PARTICIPANT: Show static counts
                <>
                  <div
                    className="flex justify-between py-1.5"
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    <span className="text-muted-foreground">Groups</span>
                    <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                      {groupCount} assigned
                    </span>
                  </div>
                  <div
                    className="flex justify-between py-1.5"
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    <span className="text-muted-foreground">Licenses</span>
                    <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                      {licenseCount} allocated
                    </span>
                  </div>
                </>
              ) : (
                // EXISTING PARTICIPANT: Show change deltas
                <>
                  <div
                    className="flex justify-between py-1.5"
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    <span className="text-muted-foreground">Groups</span>
                    <span
                      style={{
                        fontWeight: 'var(--font-weight-medium)',
                        color:
                          groupCount !== initialGroupCount
                            ? 'hsl(var(--primary))'
                            : 'hsl(var(--muted-foreground) / 0.5)',
                      }}
                    >
                      {groupCount === initialGroupCount
                        ? 'No change'
                        : `+${groupCount - initialGroupCount} added`}
                    </span>
                  </div>
                  <div
                    className="flex justify-between py-1.5"
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    <span className="text-muted-foreground">Licenses</span>
                    <span
                      style={{
                        fontWeight: 'var(--font-weight-medium)',
                        color:
                          licenseCount !== initialLicenseCount
                            ? 'hsl(var(--primary))'
                            : 'hsl(var(--muted-foreground) / 0.5)',
                      }}
                    >
                      {licenseCount === initialLicenseCount
                        ? 'No change'
                        : `+${licenseCount - initialLicenseCount} allocated`}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-1.5">
              <button
                className="w-full py-2.5 px-4 rounded-lg border-2 border-primary text-primary bg-transparent hover:opacity-90 transition-opacity"
                style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}
                onClick={close}
                data-intercom-target="confirmation-add-another-participant"
              >
                <T kind="Add Another Participant" />
              </button>
              <button
                className="w-full py-2 text-muted-foreground hover:text-foreground transition-colors"
                style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                onClick={returnToList}
                data-intercom-target="confirmation-return-to-list"
              >
                <T kind="Return to Participant List" />
              </button>
            </div>
          </div>
        </Modal>
      );
  }
}

export default observer(ParticipantAdded);
